import React, { useEffect, useState } from "react";
import { useDrag, useDrop } from "react-dnd";

import dragIcon from "assets/icons/drag-icon.svg";
import descktopIcon from "assets/icons/desc.svg";
import phoneIcon from "assets/icons/mob.svg";
import SwitchBtn from "../../../../components/buttons/SwitchBtn";
import instance from "../../../../api/apiClient";
import { apiRoutes } from "../../../../configs/apiRoutes";

import NoImage from "assets/images/image_not_found.webp";
import { getGameImage } from "helpers/getGameImage";
import { Options } from "components/table/components/Options/Options";
import CopyToClipboard from "components/common/CopyToClipboard/CopyToClipboard";
import { Checkbox } from "components/table/components/Checkbox/Checkbox";
import Loader from "components/global/Loader";

type Props = {
  item: any;
  index: number;
  priority: number;
  moveItem: (gameId: number, targetPriority: number) => void;
  onRemove: (gameId: number) => void;
  isSelected: boolean;
  onSelect: (gameId: number) => void;
  isReadOnly?: boolean;
};

const TagsOrderListTabItem: React.FC<Props> = ({
  item,
  index,
  priority,
  moveItem,
  onRemove,
  isSelected,
  onSelect,
  isReadOnly = false,
}) => {
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: "order_item",
      item: { index, gameId: item.id },
      canDrag: !isReadOnly,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [index, isReadOnly],
  );

  const [, drop] = useDrop(
    () => ({
      accept: "order_item",
      canDrop: () => !isReadOnly,
      drop: (draggedItem: { index: number; gameId: number }) => {
        if (!isReadOnly && draggedItem.index !== index) {
          moveItem(draggedItem.gameId, priority);
          draggedItem.index = index;
        }
      },
    }),
    [index, isReadOnly, moveItem, priority],
  );

  const [order, setOrder] = useState<string>(priority.toString());
  const [isActive, setIsActive] = useState(item.isActive);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    setOrder(priority.toString());
  }, [priority]);

  useEffect(() => {
    if (item?.isActive !== undefined && item?.isActive !== null) {
      setIsActive(item.isActive);
    }
  }, [item?.isActive]);

  const handleBlur = () => {
    if (isReadOnly) return;

    const parsed = parseInt(order, 10);
    if (!Number.isNaN(parsed)) {
      if (parsed > 0 && parsed !== priority) {
        moveItem(item.id, parsed);
      } else {
        setOrder(priority.toString());
      }
    } else {
      setOrder(priority.toString());
    }
  };

  const toggleActive = async () => {
    if (isReadOnly || isUpdating) return;

    const newValue = !isActive;
    setIsUpdating(true);
    setIsActive(newValue);

    try {
      const updated = await updateGame(newValue);
      if (!updated) {
        setIsActive(!newValue);
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const updateGame = async (value: boolean) => {
    if (isReadOnly) return false;

    const data = new FormData();
    data.append("isActive", value ? "1" : "0");

    try {
      await instance.put(`${apiRoutes.gameUpdate}/${item.id}`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return true;
    } catch (error) {
      return false;
    }
  };

  return (
    <div
      className={`item game-order-item ${isUpdating ? "is-processing" : ""}`}
      key={item.id}
      ref={(node) => drag(drop(node))}
      style={{ opacity: isDragging ? 0.5 : 1 }}
      aria-busy={isUpdating}
    >
      <div className="game-selection-checkbox">
        <Checkbox
          id={item.id}
          checked={isSelected}
          disabled={isReadOnly}
          onClick={(id) => onSelect(Number(id))}
        />
      </div>
      <img
        src={dragIcon}
        alt=""
        className="dragIcon"
        style={{ opacity: isReadOnly ? 0.45 : 1 }}
      />
      <div className="order">
        <input
          type="number"
          value={order}
          disabled={isReadOnly}
          onChange={(e) => setOrder(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.blur();
            }
          }}
        />
      </div>
      <div className="show">
        <SwitchBtn
          isActive={isActive}
          handleClick={toggleActive}
          disabled={isReadOnly || isUpdating}
        />
      </div>
      <div className="image">
        <img
          src={getGameImage(item) || NoImage}
          alt=""
          className={`game-img ${isActive ? "active" : "no-active"}`}
          onError={(e) => {
            e.currentTarget.src = NoImage;
            e.currentTarget.onerror = null;
          }}
        />
      </div>
      <div className="content">
        <CopyToClipboard text={item.title} />
        <span title={item.title}>{item.title}</span>
      </div>
      <div className="provider">{item.provider.title}</div>
      <div className="devices row">
        <div
          className={`dev-item ${item.devices.includes("desk") ? "" : "hidden"}`}
        >
          <img src={descktopIcon} alt="" />
        </div>
        <div
          className={`dev-item ${item.devices.includes("mob") ? "" : "hidden"}`}
        >
          <img src={phoneIcon} alt="" />
        </div>
      </div>
      <div className="options">
        <div
          aria-disabled={isReadOnly}
          style={{
            cursor: isReadOnly ? "not-allowed" : undefined,
            opacity: isReadOnly ? 0.45 : 1,
            pointerEvents: isReadOnly ? "none" : undefined,
          }}
        >
          <Options
            id={item.id}
            hasDelete
            instantDelete
            hasEdit={false}
            onDelete={(id) => onRemove(Number(id))}
          />
        </div>
      </div>
      {isUpdating && (
        <div className="game-order-item__processing-spinner">
          <Loader size={10} />
        </div>
      )}
    </div>
  );
};

export default TagsOrderListTabItem;
